import { ENTITY_TYPE_HREF } from 'app/pages/loanApplication/tabs/borrowers/EditEntityModal/EditEntityModal.hrefs';

import { LoanApplicationTabRouteParam } from 'constants/loanApplication';

import { ENTITY_REFERENCE_TYPE } from './TeamChat.entityReferences.utils';

const APPLICATIONS_SEGMENT = 'applications';
const MANAGEMENT_SEGMENT = 'management';

// Positional offsets of the loan-editor path relative to the `applications` segment:
// applications / :applicationId / management / :tab / :subtab / :entityId / …
const PATH_OFFSET = {
  APPLICATION_ID: 1,
  MANAGEMENT: 2,
  TAB: 3,
  SUBTAB: 4,
  ENTITY_ID: 5,
};

const getUrlPathname = url => {
  try {
    return new URL(url).pathname;
  } catch {
    // Relative loan-editor paths (`/applications/…`) throw the absolute-URL parser — strip the
    // query/hash by hand and use the raw path.
    return url.split(/[?#]/)[0];
  }
};

/**
 * Parses a loan-editor URL down to the ONE entity it addresses by a row-invertible id. Today that
 * is only a BORROWER (its id is a path segment); financial-view URLs encode a type + `all` marker,
 * not a specific row, so they intentionally return null and paste as plain text. Returns
 * `{ applicationId, entityType, entityId }` or null.
 */
const parseLoanEditorEntityUrl = rawText => {
  const pathname = getUrlPathname(rawText);
  const segments = pathname.split('/').filter(Boolean);
  const applicationsIndex = segments.indexOf(APPLICATIONS_SEGMENT);

  if (applicationsIndex === -1) {
    return null;
  }

  const applicationId = segments[applicationsIndex + PATH_OFFSET.APPLICATION_ID];
  const managementSegment = segments[applicationsIndex + PATH_OFFSET.MANAGEMENT];
  const tab = segments[applicationsIndex + PATH_OFFSET.TAB];
  const subtab = segments[applicationsIndex + PATH_OFFSET.SUBTAB];
  const entityId = segments[applicationsIndex + PATH_OFFSET.ENTITY_ID];

  if (!applicationId || managementSegment !== MANAGEMENT_SEGMENT) {
    return null;
  }

  if (
    tab === LoanApplicationTabRouteParam.BORROWERS &&
    subtab === ENTITY_TYPE_HREF.BORROWER &&
    entityId
  ) {
    return { applicationId, entityType: ENTITY_REFERENCE_TYPE.BORROWER, entityId };
  }

  return null;
};

/**
 * Resolves a pasted string to an entity reference `{ entityType, entityId, label }`, or null when it
 * should paste as-is. Conservative by design: the trimmed paste must be exactly ONE URL (no
 * surrounding text), point at THIS chat's loan (case-insensitive), address a row-invertible entity,
 * and resolve to a live candidate — so the composer inserts the current label as an alias instead of
 * a raw URL. Anything else returns null.
 */
export const resolvePastedEntityReference = ({ rawText, applicationId, candidatesByType }) => {
  const trimmed = (rawText ?? '').trim();

  if (!trimmed || /\s/.test(trimmed)) {
    return null;
  }

  const parsed = parseLoanEditorEntityUrl(trimmed);

  if (
    !parsed ||
    !applicationId ||
    parsed.applicationId.toLowerCase() !== applicationId.toLowerCase()
  ) {
    return null;
  }

  const candidate = (candidatesByType?.[parsed.entityType] ?? []).find(
    entry => entry.entityId?.toLowerCase() === parsed.entityId.toLowerCase(),
  );

  if (!candidate) {
    return null;
  }

  return {
    entityType: parsed.entityType,
    entityId: candidate.entityId,
    label: candidate.label,
  };
};
